import React, { useState, useRef, useEffect } from "react";
import { Plus, Trash2, Calendar, Download, Upload } from "lucide-react";
import { format } from "date-fns";
import { useReactToPrint } from "react-to-print";
import { cn } from "../../lib/utils";
import defaultLogo from "../../assets/logo/logo.png";

const defaultQuoteTerms = `1. Delivery Timeline: The total anticipated duration for completion is 25 working days (estimated end date: 05/05/2026, assuming an April 1st, 2026 start). The first officially countable working day of this timeline will be the day immediately following the successful receipt of the initial advance payment.
2. Payment Terms: Remittance is structured in three equal phases. An initial advance constitutes the first payment. The subsequent phase is payable upon achieving a progressive milestone agreed upon by both the Client and Codescap, with the final balance due strictly upon 100% successful development of the project.
3. Scope: Any additional features/revisions outside the agreed initial scope will be evaluated and billed separately.`;

const defaultInvoiceTerms = `Valid for 30 days. Payment due within 15 days of invoice.`;

function useStickyState(defaultValue, key) {
  const [value, setValue] = useState(() => {
    try {
      const stickyValue = window.localStorage.getItem(key);
      return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
    } catch (e) {
      return defaultValue;
    }
  });

  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
}

export default function QuotationBuilder() {
  const [items, setItems] = useStickyState([], "cs_items");

  const [newItem, setNewItem] = useState({ name: "", quantity: "", rate: "" });

  const [client, setClient] = useStickyState(
    {
      name: "",
      company: "",
      contact: "",
    },
    "cs_client",
  );

  const [docInfo, setDocInfo] = useStickyState(
    {
      number: "CSQ123",
      date: format(new Date(), "yyyy-MM-dd"),
      discount: 0,
      discountType: "fixed",
    },
    "cs_docInfo",
  );

  const [documentType, setDocumentType] = useStickyState(
    "Quotation",
    "cs_documentType",
  );
  const [showRateCols, setShowRateCols] = useStickyState(
    true,
    "cs_showRateCols",
  );

  // Logos usually too large for stringify, so isolating them to regular state
  const [useCustomLogo, setUseCustomLogo] = useState(false);
  const [customLogoBase64, setCustomLogoBase64] = useState(null);

  const [contactPhone, setContactPhone] = useStickyState(
    "+91 9037162751",
    "cs_contactPhone",
  );
  const [contactEmail, setContactEmail] = useStickyState(
    "support@codescap.com",
    "cs_contactEmail",
  );

  const [currency, setCurrency] = useStickyState("INR", "cs_currency");
  const [exchangeRate, setExchangeRate] = useStickyState("", "cs_exchangeRate");
  const [fetchLiveRate, setFetchLiveRate] = useStickyState(
    true,
    "cs_fetchLiveRate",
  );

  const [quoteTerms, setQuoteTerms] = useStickyState(
    defaultQuoteTerms,
    "cs_quoteTerms",
  );
  const [invoiceTerms, setInvoiceTerms] = useStickyState(
    defaultInvoiceTerms,
    "cs_invoiceTerms",
  );

  // Dynamic derivation of terms dependent on current toggle
  const terms = documentType === "Quotation" ? quoteTerms : invoiceTerms;
  const setTerms =
    documentType === "Quotation" ? setQuoteTerms : setInvoiceTerms;

  const previewRef = useRef(null);

  // Syncing Number specifically to the format requested
  useEffect(() => {
    localStorage.setItem("lastQuotationNumber", docInfo.number);
  }, [docInfo.number]);

  useEffect(() => {
    if (currency === "USD" && fetchLiveRate) {
      fetch("https://open.er-api.com/v6/latest/USD")
        .then((res) => res.json())
        .then((data) => {
          if (data && data.rates && data.rates.INR) {
            setExchangeRate(data.rates.INR.toFixed(2));
          }
        })
        .catch((err) => console.error("Could not fetch exchange rate", err));
    }
  }, [currency, fetchLiveRate]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomLogoBase64(reader.result);
        setUseCustomLogo(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const addItem = () => {
    if (!newItem.name || !newItem.rate) return;
    const item = {
      id: Math.random().toString(36).substring(2, 9),
      name: newItem.name,
      quantity: parseFloat(newItem.quantity) || 1,
      rate: parseFloat(newItem.rate) || 0,
    };
    setItems([...items, item]);
    setNewItem({ name: "", quantity: "", rate: "" });
  };

  const removeItem = (id) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const subtotal = items.reduce(
    (acc, item) => acc + item.quantity * item.rate,
    0,
  );
  const discountAmount =
    docInfo.discountType === "percent"
      ? (subtotal * docInfo.discount) / 100
      : docInfo.discount;
  const total = subtotal - discountAmount;

  const handlePrint = useReactToPrint({
    contentRef: previewRef,
    documentTitle: `${documentType}_${docInfo.number}`,
    pageStyle: `
      @page {
        size: auto;
        margin: 15mm;
      }
      @page :first {
        margin-top: 0mm;
      }
      @media print {
        html, body {
          height: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          background-color: white;
        }
        .print-container {
          display: flex !important;
          flex-direction: column !important;
          min-height: 100% !important;
        }
        .print-content {
          flex-grow: 1 !important;
        }
        tr {
          break-inside: avoid !important;
          -webkit-column-break-inside: avoid;
          page-break-inside: avoid !important;
        }
        .break-inside-avoid {
          break-inside: avoid !important;
          -webkit-column-break-inside: avoid;
          page-break-inside: avoid !important;
        }
        thead {
          display: table-header-group !important;
        }
        table {
          width: 100% !important;
          table-layout: auto !important;
        }
      }
    `,
  });

  const formatCcy = (amount) => {
    if (currency === "USD") return `$${amount.toLocaleString()}`;
    return `₹${amount.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] p-4 md:p-8 font-sans text-slate-900">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Side: Form */}
        <div className="space-y-8">
          <h1 className="text-3xl font-bold tracking-tight">
            Codescap Builder
          </h1>

          <section className="space-y-4 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h2 className="text-xl font-semibold">General Settings</h2>

            <div className="grid grid-cols-1 flex-row sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1 sm:col-span-2">
                <label className="text-sm font-medium text-slate-600">
                  Document Type
                </label>
                <select
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                >
                  <option value="Quotation">Quotation</option>
                  <option value="Invoice">Invoice</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600">
                  Currency
                </label>
                <select
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option value="INR">INR (₹)</option>
                  <option value="USD">USD ($)</option>
                </select>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-slate-600">
                    Rate (USD-INR)
                  </label>
                  {currency === "USD" && (
                    <label className="flex items-center space-x-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={fetchLiveRate}
                        onChange={(e) => setFetchLiveRate(e.target.checked)}
                        className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-[10px] font-medium text-slate-500">
                        Live API
                      </span>
                    </label>
                  )}
                </div>
                <input
                  type="number"
                  disabled={currency !== "USD" || fetchLiveRate}
                  placeholder={currency === "USD" ? "e.g. 83.5" : "-"}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600">
                  Our Phone
                </label>
                <input
                  type="text"
                  className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600">
                  Our Email
                </label>
                <input
                  type="text"
                  className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-slate-600">
                    Custom Logo
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useCustomLogo}
                      onChange={(e) => setUseCustomLogo(e.target.checked)}
                      className="w-3.5 h-3.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-xs font-medium text-slate-500">
                      Enable
                    </span>
                  </label>
                </div>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-center gap-2 text-slate-500 hover:bg-slate-100 transition-colors">
                    <Upload size={18} />{" "}
                    <span className="text-sm font-medium">Upload Image</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-2">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showRateCols}
                  onChange={(e) => setShowRateCols(e.target.checked)}
                  className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-600">
                  Show Hours & Rate Columns
                </span>
              </label>
            </div>
          </section>

          {/* Add Item Section */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">Add Item</h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
              <div
                className={cn(
                  "space-y-1",
                  showRateCols ? "sm:col-span-2" : "sm:col-span-3",
                )}
              >
                <label className="text-sm font-medium text-slate-600">
                  Item Name
                </label>
                <input
                  type="text"
                  placeholder="e.g., Web Development"
                  className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={newItem.name}
                  onChange={(e) =>
                    setNewItem({ ...newItem, name: e.target.value })
                  }
                />
              </div>

              {showRateCols ? (
                <>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-600">
                      Hours
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 10"
                      className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                      value={newItem.quantity}
                      onChange={(e) =>
                        setNewItem({ ...newItem, quantity: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-600">
                      Rate ({currency})
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="e.g. 50"
                        className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                        value={newItem.rate}
                        onChange={(e) =>
                          setNewItem({ ...newItem, rate: e.target.value })
                        }
                      />
                      <button
                        onClick={addItem}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 transition-colors font-medium whitespace-nowrap"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-600">
                    Amount ({currency})
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="e.g. 500"
                      className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                      value={newItem.rate}
                      onChange={(e) =>
                        setNewItem({ ...newItem, rate: e.target.value })
                      }
                    />
                    <button
                      onClick={addItem}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors font-medium whitespace-nowrap"
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Items List (Editable) */}
            {items.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-50 border-bottom border-slate-200">
                    <tr>
                      <th className="p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Item
                      </th>
                      {showRateCols && (
                        <>
                          <th className="p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Qty
                          </th>
                          <th className="p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Rate
                          </th>
                        </>
                      )}

                      <th className="p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Total
                      </th>

                      <th className="p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="p-3 font-medium">{item.name}</td>
                        {showRateCols && (
                          <>
                            <td className="p-3">{item.quantity}</td>
                            <td className="p-3">{formatCcy(item.rate)}</td>
                          </>
                        )}
                        <td className="p-3 font-semibold text-blue-600">
                          {formatCcy(item.quantity * item.rate)}
                        </td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => removeItem(item.id)}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Client Details */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">Client Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600">
                  Name
                </label>
                <input
                  type="text"
                  placeholder="Enter Client Name"
                  className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={client.name}
                  onChange={(e) =>
                    setClient({ ...client, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600">
                  Company
                </label>
                <input
                  type="text"
                  placeholder="Enter Company Name"
                  className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={client.company}
                  onChange={(e) =>
                    setClient({ ...client, company: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600">
                  Contact
                </label>
                <input
                  type="text"
                  placeholder="Email or Phone"
                  className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={client.contact}
                  onChange={(e) =>
                    setClient({ ...client, contact: e.target.value })
                  }
                />
              </div>
            </div>
          </section>

          {/* Document Info */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">Document Info</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600">
                  {documentType} Number
                </label>
                <input
                  type="text"
                  className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={docInfo.number}
                  onChange={(e) =>
                    setDocInfo({ ...docInfo, number: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600">
                  Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all pl-10"
                    value={docInfo.date}
                    onChange={(e) =>
                      setDocInfo({ ...docInfo, date: e.target.value })
                    }
                  />
                  <Calendar
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={18}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600 flex justify-between">
                  Discount
                  <select
                    className="text-xs bg-transparent border-none focus:ring-0 cursor-pointer text-blue-600 font-semibold"
                    value={docInfo.discountType}
                    onChange={(e) =>
                      setDocInfo({ ...docInfo, discountType: e.target.value })
                    }
                  >
                    <option value="fixed">Fixed ({currency})</option>
                    <option value="percent">Percent (%)</option>
                  </select>
                </label>
                <input
                  type="number"
                  placeholder="0"
                  className="w-full p-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={docInfo.discount}
                  onChange={(e) =>
                    setDocInfo({
                      ...docInfo,
                      discount: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </div>
          </section>

          {/* Terms & Conditions */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">Terms & Conditions</h2>
            <textarea
              placeholder="Enter your terms and conditions here... (e.g., Valid for 30 days)"
              className="w-full p-4 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all min-h-[120px] resize-none"
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
            />
          </section>

          {currency === "USD" && exchangeRate && (
            <div className="bg-blue-50 text-blue-800 p-4 rounded-xl border border-blue-100 flex justify-between items-center">
              <span className="font-medium text-sm">
                Estimated Total in INR (Admin Reference Only)
              </span>
              <span className="text-xl font-bold">
                ₹
                {(total * parseFloat(exchangeRate)).toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          )}

          <button
            onClick={() => handlePrint()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl flex items-center justify-center gap-3 transition-all font-bold text-lg shadow-lg shadow-blue-200"
          >
            Print/Download PDF <Download size={22} />
          </button>
        </div>

        {/* Right Side: Preview */}
        <div className="sticky top-8">
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden print:shadow-none print:border-none print:rounded-none print-container"
            ref={previewRef}
          >
            {/* Preview Header */}
            <div className="bg-blue-50 p-8 border-b border-slate-100">
              <div className="flex flex-col items-center text-center space-y-4">
                <img
                  src={
                    useCustomLogo && customLogoBase64
                      ? customLogoBase64
                      : defaultLogo
                  }
                  alt="codescap-logo"
                  className="h-[40px] w-auto object-contain"
                />
                <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm font-medium text-slate-600">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Phone:</span>{" "}
                    {contactPhone}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Email:</span>{" "}
                    {contactEmail}
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Content */}
            <div className="p-8 space-y-10 print-content">
              {/* Addresses */}
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">
                    {documentType} To:
                  </h3>
                  <div className="space-y-1">
                    <p className="font-bold text-slate-800">
                      {client.name || "Client Name"}
                    </p>
                    <p className="text-sm text-slate-600">
                      {client.company || "Company Name"}
                    </p>
                    <p className="text-sm text-slate-600">
                      {client.contact || "Contact Info"}
                    </p>
                  </div>
                </div>
                <div className="space-y-3 text-right">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">
                    {documentType} From:
                  </h3>
                  <div className="space-y-1">
                    <p className="font-bold text-slate-800">
                      Codescap IT Solutions
                    </p>
                    <p className="text-sm text-slate-600">Roshni Plaza</p>
                    <p className="text-sm text-slate-600">Angadippuram</p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <h2 className="text-2xl font-black tracking-[0.3em] uppercase text-slate-800 border-y-2 border-slate-100 py-2 inline-block px-8">
                  {documentType}
                </h2>
              </div>

              <div className="flex justify-between items-center bg-slate-50 p-4 rounded-lg">
                <div className="space-y-1">
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    {documentType} No:
                  </h3>
                  <p className="font-bold text-slate-800">{docInfo.number}</p>
                </div>
                <div className="space-y-1 text-right">
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    Date:
                  </h3>
                  <p className="font-bold text-slate-800">{docInfo.date}</p>
                </div>
              </div>

              {/* Items Table */}
              <div className="rounded-xl border border-slate-100 overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-[#1e293b] text-white">
                    <tr>
                      <th className="p-4 text-xs font-bold uppercase tracking-wider w-12">
                        No.
                      </th>
                      <th className="p-4 text-xs font-bold uppercase tracking-wider">
                        Item
                      </th>
                      {showRateCols && (
                        <>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider text-right">
                            Hours
                          </th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider text-right">
                            Rate
                          </th>
                        </>
                      )}

                      <th className="p-4 text-xs font-bold uppercase tracking-wider text-right">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((item, index) => (
                      <tr key={item.id}>
                        <td className="p-4 text-sm text-slate-500">
                          {index + 1}
                        </td>
                        <td className="p-4 text-sm font-semibold text-slate-800">
                          {item.name}
                        </td>

                        {showRateCols && (
                          <>
                            <td className="p-4 text-sm text-slate-600 text-right">
                              {item.quantity}
                            </td>
                            <td className="p-4 text-sm text-slate-600 text-right">
                              {formatCcy(item.rate)}
                            </td>
                          </>
                        )}

                        <td className="p-4 text-sm font-bold text-slate-800 text-right">
                          {formatCcy(item.quantity * item.rate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end break-inside-avoid">
                <div className="w-64 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500 font-medium">
                      Subtotal:
                    </span>
                    <span className="font-bold text-slate-800">
                      {formatCcy(subtotal)}
                    </span>
                  </div>
                  {docInfo.discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500 font-medium">
                        Discount:
                      </span>
                      <span className="font-bold text-red-500">
                        -{formatCcy(discountAmount)}
                      </span>
                    </div>
                  )}
                  <div className="h-px bg-slate-200" />
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-black uppercase tracking-wider text-slate-800">
                      Total:
                    </span>
                    <span className="text-xl font-black text-blue-700">
                      {formatCcy(total)}
                    </span>
                  </div>
                  <p className="text-[10px] text-right text-slate-400 font-medium italic mt-2 leading-relaxed">
                    {currency === "INR" ? (
                      "All the amounts mentioned are in INR."
                    ) : (
                      <>
                        All amounts mentioned are in USD.
                        <br />
                        {exchangeRate
                          ? `(Exchange Rate: 1 USD = ₹${exchangeRate}) `
                          : ""}
                        On the date of generating this invoice.
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Bank Details styled specifically based on latest requirement */}
              <div className="pt-8 border-t border-slate-100 break-inside-avoid">
                <div className="bg-slate-50 rounded-lg py-4 px-6 flex justify-between items-center border border-slate-200">
                  <div className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                      Acc No
                    </span>
                    <span className="font-black text-sm text-slate-800 tracking-wide">
                      99980101054273
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 text-center">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                      IFSC
                    </span>
                    <span className="font-black text-sm text-slate-800 tracking-wide">
                      FDRL0001537
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 text-right">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                      UPI Id
                    </span>
                    <span className="font-black text-sm text-slate-800 tracking-wide uppercase">
                      AJMALPERUMPULLY11@OKSBI
                    </span>
                  </div>
                </div>
              </div>

              {/* Terms */}
              {terms?.trim() && (
                <div className="space-y-2 break-inside-avoid">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400">
                    Terms and Conditions
                  </h4>
                  <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">
                    {terms}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-blue-50 p-4 text-center border-t border-slate-100 break-inside-avoid">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                A subsidiary of Foodoor Enterprises LLP
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
